import { useEffect, useRef, useState } from 'react';
import { findAdjacentAnchors, getShortestPath } from './utils';
import { ANCHOR_POINTS_MAP, DISTANCE_DAMPER, TOTAL_IMAGES } from './constants';
import svgData from 'assets/json/paths.json';
import { ArrowLeft } from 'assets/icons/ArrowLeft';
import { ArrowRight } from 'assets/icons/ArrowRight';
import { imagePath, useImagePreloader } from './hooks/useImagePreloader';
import { BuildingHoverCard } from './components/BuildingHoverCard';

import './styles.scss';
import { Flower } from 'assets/icons/Flower';
import { Waves } from 'assets/icons/Waves';
import { Basketball } from 'assets/icons/Basketball';
import { AMENITY_ADAPTER } from 'consts';

const isWideScreen = window.innerWidth / window.innerHeight >= 2.5;

const {
  props: svgProps,
  buildings,
  iconTemplates,
  apartmentTemplates,
  pathGroups,
  buildingName,
} = svgData;

export const BuildingRotation = ({
  onShowApartmentInfo,
  onResetFilter,
  filter,
  onSelectScene,
  clickedApartment,
  setClickedApartment,
  isViewingApartmentInfo,
}) => {
  const [building, setBuilding] = useState('VR3');

  // Building states
  const { preloadBuilding, isLoaded } = useImagePreloader();
  const [isDragging, setIsDragging] = useState(false);
  const [previousMouseX, setPreviousMouseX] = useState(0);
  const [previousTouchX, setPreviousTouchX] = useState(0);
  const [image, setImage] = useState(ANCHOR_POINTS_MAP['VR3'][0]); // starting image index
  const [isAnimating, setIsAnimating] = useState(false);

  // Detect if device supports touch
  const isTouchDevice = useRef(
    'ontouchstart' in window || navigator.maxTouchPoints > 0
  );

  // Hover behavior states
  const [hoverData, setHoverData] = useState({
    isHovering: false,
    event: null,
  });
  const [tooltipData, setTooltipData] = useState({
    isHovering: false,
    label: null,
    event: null,
  });
  const svgRef = useRef(null);

  const dragDistance = useRef(0);
  const dragDirection = useRef('right');
  const animationRef = useRef(null);
  const clickedTimeoutRef = useRef(null);
  const [isFading, setIsFading] = useState(false);
  const wasViewingApartmentRef = useRef(false);

  // Helper function to cancel ongoing animation
  const cancelAnimation = () => {
    if (animationRef.current) {
      if (typeof animationRef.current === 'number') {
        clearTimeout(animationRef.current);
      } else {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = null;
      setIsAnimating(false);
    }
  };

  // Cancel animation and reset image when building changes
  useEffect(() => {
    cancelAnimation();
    setImage(ANCHOR_POINTS_MAP[building][0]);
    dragDistance.current = 0;
  }, [building]);

  // Clear clicked apartment timeout on unmount
  useEffect(() => {
    return () => {
      if (clickedTimeoutRef.current) {
        clearTimeout(clickedTimeoutRef.current);
      }
    };
  }, []);

  // Trigger fade animation when user comes back from apartment info
  useEffect(() => {
    // If user was viewing apartment info and now is not, start fade animation
    if (
      wasViewingApartmentRef.current &&
      !isViewingApartmentInfo &&
      clickedApartment
    ) {
      setIsFading(true);

      // Auto-clear after 2 seconds
      if (clickedTimeoutRef.current) {
        clearTimeout(clickedTimeoutRef.current);
      }
      clickedTimeoutRef.current = setTimeout(() => {
        setClickedApartment(null);
        setIsFading(false);
      }, 2500);
    }

    // Track the previous state
    wasViewingApartmentRef.current = isViewingApartmentInfo;

    // Reset when clicked apartment is cleared
    if (!clickedApartment) {
      setIsFading(false);
    }
  }, [isViewingApartmentInfo, clickedApartment, setClickedApartment]);

  // Animation function to smoothly transition between images
  const animateToTarget = (targetImage) => {
    cancelAnimation();

    const path = getShortestPath(image, targetImage);

    // If already at target or path is too short, don't animate
    if (path.length <= 1) {
      return;
    }

    let currentStep = 0;
    const animationSpeed = 50; // milliseconds between frames

    setIsAnimating(true);

    const animate = () => {
      if (currentStep < path.length - 1) {
        currentStep++;
        setImage(path[currentStep]);

        animationRef.current = setTimeout(() => {
          requestAnimationFrame(animate);
        }, animationSpeed);
      } else {
        setIsAnimating(false);
        animationRef.current = null;
      }
    };

    // Start animation
    requestAnimationFrame(animate);
  };

  const handleMouseDown = (e) => {
    // Prevent interaction while animating
    if (isAnimating) {
      return;
    }

    // Implement mouse down logic
    setIsDragging(true);
    setPreviousMouseX(e.clientX);
  };

  const handleMouseUp = (e) => {
    // Only snap to anchor if we were actually dragging
    if (!isDragging) {
      return;
    }

    // Implement mouse up logic
    setIsDragging(false);
    dragDistance.current = 0;

    const { nearest } = findAdjacentAnchors(image, ANCHOR_POINTS_MAP[building]);
    animateToTarget(nearest);
  };

  const handleDragDirectionChange = (deltaX) => {
    const direction = deltaX > 0 ? 'right' : 'left';

    if (dragDirection.current !== direction) {
      dragDirection.current = direction;
      dragDistance.current = 0;
    } else {
      dragDistance.current += deltaX;
    }
  };

  const onSliderNav = (e) => {
    // Prevent navigation while animating
    if (isAnimating) {
      return;
    }

    const type = e.currentTarget.getAttribute('data-type');

    if (!type) return;

    const anchorIndex = ANCHOR_POINTS_MAP[building].indexOf(image);

    if (anchorIndex === -1) return; // Current image is not an anchor point

    if (type === 'prev') {
      const targetAnchor =
        ANCHOR_POINTS_MAP[building][
          anchorIndex - 1 >= 0
            ? anchorIndex - 1
            : ANCHOR_POINTS_MAP[building].length - 1
        ];
      return animateToTarget(targetAnchor);
    }

    if (type === 'next') {
      const targetAnchor =
        ANCHOR_POINTS_MAP[building][
          anchorIndex + 1 >= ANCHOR_POINTS_MAP[building].length
            ? 0
            : anchorIndex + 1
        ];
      return animateToTarget(targetAnchor);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const currentMouseX = e.clientX;
    const deltaX = currentMouseX - previousMouseX;
    handleDragDirectionChange(deltaX);
    // Update previous position for next move event
    setPreviousMouseX(currentMouseX);
    const imageGap =
      dragDistance.current > 0
        ? Math.ceil((dragDistance.current / DISTANCE_DAMPER) % TOTAL_IMAGES)
        : Math.floor((dragDistance.current / DISTANCE_DAMPER) % TOTAL_IMAGES);

    setImage((prev) => {
      const targetImage = prev + imageGap;
      if (targetImage < 0) return TOTAL_IMAGES - 1;
      if (targetImage > TOTAL_IMAGES - 1) return 0;
      return targetImage;
    });
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    // Prevent interaction while animating
    if (isAnimating) {
      return;
    }

    setIsDragging(true);
    setPreviousTouchX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    // Only snap to anchor if we were actually dragging
    if (!isDragging) {
      return;
    }

    setIsDragging(false);
    dragDistance.current = 0;

    const { nearest } = findAdjacentAnchors(image, ANCHOR_POINTS_MAP[building]);
    animateToTarget(nearest);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;

    const currentTouchX = e.touches[0].clientX;
    const deltaX = currentTouchX - previousTouchX;
    handleDragDirectionChange(deltaX);

    // Update previous position for next move event
    setPreviousTouchX(currentTouchX);

    const imageGap =
      dragDistance.current > 0
        ? Math.ceil((dragDistance.current / DISTANCE_DAMPER) % TOTAL_IMAGES)
        : Math.floor((dragDistance.current / DISTANCE_DAMPER) % TOTAL_IMAGES);

    setImage((prev) => {
      const targetImage = prev + imageGap;
      if (targetImage < 0) return TOTAL_IMAGES - 1;
      if (targetImage > TOTAL_IMAGES - 1) return 0;
      return targetImage;
    });
  };

  const handleChangeBuilding = (e) => {
    // Prevent building switch while animating
    if (isAnimating) {
      return;
    }

    const newBuilding = e.currentTarget.getAttribute('data-building');
    if (newBuilding && newBuilding !== building) {
      setBuilding(newBuilding);
      onResetFilter();
    }
  };

  useEffect(() => {
    // Preload current building
    if (!isLoaded(building)) {
      preloadBuilding(building);
    }

    // Preload the other building
    const otherBuilding = building === 'VR3' ? 'VR2' : 'VR3';
    if (!isLoaded(otherBuilding)) {
      preloadBuilding(otherBuilding);
    }
  }, [building, isLoaded, preloadBuilding]);

  // Get towers for the current building
  const towers = buildings[building] || [];

  // Check filter type for showing/hiding apartments vs amenities
  const showApartments = !filter.type || filter.type === 'can-ho';
  const showAmenities = filter.type === 'tien-ich';

  // Helper function to check if apartment matches filter
  const matchesFilter = (template) => {
    const { apartmentType, floor, usageArea } = filter;

    // If no filter is applied, show all
    if (!apartmentType && !floor && !usageArea) {
      return true;
    }

    // Check apartment type
    if (apartmentType) {
      if (apartmentType === '1pn') {
        if (template.type !== '1pn' && template.type !== 'studio') {
          return false;
        }
      } else if (template.type !== apartmentType) {
        return false;
      }
    }

    // Check floor range
    if (floor && template.floor !== floor) {
      return false;
    }

    // Check usage area
    if (usageArea && template.usageArea > usageArea) {
      return false;
    }

    return true;
  };

  const handleTooltipMouseEnter = (event, label) => {
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    setTooltipData({
      isHovering: true,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top,
      },
      label,
    });
  };

  const handleTooltipMouseLeave = () => {
    setTooltipData({ isHovering: false, position: null, label: null });
  };

  return (
    <div
      className={`image-slider-container${isWideScreen ? ' wide-screen' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      <div className="image-slider-wrapper">
        {isLoaded && (
          <img
            // src={`/assets/images/img3/${building.toLowerCase()}/${building}_00${image}.webp`}
            src={imagePath(building, image)}
            alt={`Building ${building}-00${image}`}
            className="slider-image"
            style={{ pointerEvents: 'none' }}
          />
        )}
        <svg
          width="100%"
          height="100%"
          viewBox={svgProps.viewBox}
          preserveAspectRatio="xMidYMid slice"
          xmlns={svgProps.xmlns}
          className="svg-overlay"
          ref={svgRef}
        >
          {showApartments &&
            towers.map(({ towerName, apartments, config }) => (
              <g id={towerName} className="tower-group" key={towerName}>
                {apartments.map(
                  ({ templateId, pathGroupId }, apartmentIndex) => {
                    // Get apartment template
                    const template = apartmentTemplates[templateId];
                    if (!template) return null;

                    // Apply filter
                    if (!matchesFilter(template)) return null;

                    const { type, wallArea, usageArea, floor } = template;

                    // Get paths for this apartment from building-specific pathGroup
                    const pathGroup = pathGroupId
                      ? pathGroups[pathGroupId]
                      : {};
                    const currentPaths = pathGroup[`image${image}`] || [];

                    return (
                      <g
                        key={`${towerName}-${templateId}-${apartmentIndex}`}
                        data-type={type}
                        data-wall-area={wallArea}
                        data-usage-area={usageArea}
                        className={`${type}`}
                        id={`${towerName}-${type}-${floor}`}
                      >
                        {currentPaths.map((pathData, pathIndex) => (
                          <path
                            key={`${templateId}-${pathIndex}`}
                            d={pathData}
                            data-wall-area={wallArea}
                            data-usage-area={usageArea}
                            data-path={pathData}
                            data-floor={floor}
                            data-tower={towerName}
                            data-type={type}
                            fill={
                              clickedApartment &&
                              clickedApartment.tower === towerName &&
                              clickedApartment.type === type &&
                              clickedApartment.floor === floor &&
                              clickedApartment.pathData === pathData
                                ? config
                                  ? `${config.color}FF`
                                  : 'rgba(252, 186, 40, 1)'
                                : config
                                ? `${config.color}B2`
                                : 'rgba(252, 186, 40, 0.3)'
                            }
                            strokeWidth="0.5"
                            stroke="#000"
                            className={`svg-path floor-${floor} apartment-${type} ${
                              clickedApartment &&
                              clickedApartment.tower === towerName &&
                              clickedApartment.type === type &&
                              clickedApartment.floor === floor &&
                              clickedApartment.pathData === pathData
                                ? isFading
                                  ? `clicked-fading ${
                                      towerName === 'VR-3.2' ||
                                      towerName === 'VR-2.2'
                                        ? 'special-fading'
                                        : ''
                                    }`
                                  : `clicked ${towerName}`
                                : ''
                            }`}
                            onMouseEnter={
                              !isTouchDevice.current
                                ? (event) => {
                                    setHoverData({ isHovering: true, event });
                                  }
                                : undefined
                            }
                            onMouseLeave={
                              !isTouchDevice.current
                                ? () => {
                                    setHoverData({
                                      isHovering: false,
                                      event: null,
                                    });
                                  }
                                : undefined
                            }
                            onClick={() => {
                              // Clear any existing timeout
                              if (clickedTimeoutRef.current) {
                                clearTimeout(clickedTimeoutRef.current);
                              }

                              setIsFading(false);
                              setClickedApartment({
                                tower: towerName,
                                type,
                                floor,
                                pathData,
                              });
                              onShowApartmentInfo(
                                apartmentTemplates[templateId]
                              );
                            }}
                          />
                        ))}
                      </g>
                    );
                  }
                )}
              </g>
            ))}
          {buildingName &&
            buildingName[building][`image${image}`] &&
            buildingName[building][`image${image}`].map(({ x, y }, index) => (
              <g key={`building-name-${index}`}>
                <rect
                  x={x}
                  y={y}
                  width="70"
                  height="20"
                  fill="#A0793C"
                  rx="4"
                  ry="4"
                />
                <text
                  x={x + 35}
                  y={y + 11}
                  fill="#fff"
                  fontSize="10"
                  fontWeight="bold"
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  VIC-{building === 'VR3' ? 1 : 2}.{index + 1}
                </text>
              </g>
            ))}
          {showAmenities && (
            <>
              <AmenityIndicator
                label={AMENITY_ADAPTER.shophouse.label}
                data={iconTemplates.shophouse[building][`image${image}`]}
                icon={<Flower size={20} color="#fff" />}
                onMouseEnter={
                  !isTouchDevice.current ? handleTooltipMouseEnter : undefined
                }
                onMouseLeave={
                  !isTouchDevice.current ? handleTooltipMouseLeave : undefined
                }
                onClick={() => onSelectScene(AMENITY_ADAPTER.shophouse.scene)}
              />
              <AmenityIndicator
                label={AMENITY_ADAPTER.pool.label}
                data={iconTemplates.pool[building][`image${image}`]}
                icon={<Waves size={20} color="#fff" />}
                onMouseEnter={
                  !isTouchDevice.current ? handleTooltipMouseEnter : undefined
                }
                onMouseLeave={
                  !isTouchDevice.current ? handleTooltipMouseLeave : undefined
                }
                onClick={() => onSelectScene(AMENITY_ADAPTER.pool.scene)}
              />
              <AmenityIndicator
                label={AMENITY_ADAPTER.playground.label}
                data={iconTemplates.playground[building][`image${image}`]}
                icon={<Basketball size={20} color="#fff" />}
                onMouseEnter={
                  !isTouchDevice.current ? handleTooltipMouseEnter : undefined
                }
                onMouseLeave={
                  !isTouchDevice.current ? handleTooltipMouseLeave : undefined
                }
                onClick={() => onSelectScene(AMENITY_ADAPTER.playground.scene)}
              />
            </>
          )}
        </svg>
      </div>

      {hoverData.isHovering && !isDragging && (
        <BuildingHoverCard event={hoverData.event} />
      )}

      {tooltipData.isHovering && !isDragging && (
        <AmenityTooltip
          position={tooltipData.position}
          label={tooltipData.label}
        />
      )}

      <div className="action-btns">
        <div className="building-tab">
          <div
            disabled={isAnimating}
            data-building="VR3"
            className={`tab-item ${building === 'VR3' ? 'active' : ''}`}
            onClick={handleChangeBuilding}
          >
            Toà VIC 1
          </div>
          <div
            disabled={isAnimating}
            className={`tab-item ${building === 'VR2' ? 'active' : ''}`}
            data-building="VR2"
            onClick={handleChangeBuilding}
          >
            Toà VIC 2
          </div>
        </div>
        <button
          data-type="prev"
          className="prev"
          onClick={onSliderNav}
          disabled={isAnimating}
        >
          <ArrowLeft />
        </button>
        <button
          data-type="next"
          className="next"
          onClick={onSliderNav}
          disabled={isAnimating}
        >
          <ArrowRight />
        </button>
      </div>
    </div>
  );
};

const AmenityIndicator = ({
  data,
  icon,
  label,
  onMouseEnter,
  onMouseLeave,
  onClick,
}) => {
  if (!data) return null;
  return (
    <g
      transform={`translate(${data.x}, ${data.y})`}
      style={{ cursor: 'pointer' }}
      onMouseEnter={onMouseEnter ? (e) => onMouseEnter(e, label) : undefined}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <rect x="-4" y="-4" width="28" height="28" rx="6" ry="6" fill="#A0793C" />
      {icon}
    </g>
  );
};

const AmenityTooltip = ({ position, label = 'Hồ bơi' }) => {
  if (!position) return null;

  return (
    <span
      style={{
        position: 'fixed',
        top: position.y - 24,
        left: position.x,
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: '#fff',
        padding: '5px 10px',
        borderRadius: '4px',
        pointerEvents: 'none',
        zIndex: 1000,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};
